import React, { useState, useCallback } from 'react';
import { bool, func, object, shape, string, array, arrayOf } from 'prop-types';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { useNavigate, useLocation } from 'react-router-dom';
import omit from 'lodash/omit';
import classNames from 'classnames';

import { useConfiguration } from '../../context/configurationContext';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
import { isScrollingDisabled, manageDisableScrolling } from '../../ducks/ui.duck';
import { getListingsById } from '../../ducks/marketplaceData.duck';

import { createResourceLocatorString } from '../../util/routes';
import {
  isAnyFilterActive,
  isMainSearchTypeKeywords,
  getQueryParamNames,
  isOriginInUse,
} from '../../util/search';
import { parse } from '../../util/urlHelpers';
import { propTypes } from '../../util/types';

import Page from '../../components/Page/Page';
import { H3, H5 } from '../../components/Heading/Heading';
import { useIntl } from '../../util/reactIntl';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';

import {
  groupListingFieldConfigs,
  initialValues,
  searchParamsPicker,
  validUrlQueryParamsFromProps,
  validFilterParams,
  cleanSearchFromConflictingParams,
  createSearchResultSchema,
} from './SearchPage.shared';

import FilterComponent from './FilterComponent';
import MainPanelHeader from './MainPanelHeader/MainPanelHeader';
import SearchFiltersMobile from './SearchFiltersMobile/SearchFiltersMobile';
import SortBy from './SortBy/SortBy';
import SearchResultsPanel from './SearchResultsPanel/SearchResultsPanel';
import NoSearchResultsMaybe from './NoSearchResultsMaybe/NoSearchResultsMaybe';

import css from './SearchPage.module.css';
import MainTabNavVertical from '../../components/MainTabNavVertical/MainTabNavVertical';
import { getIsMentor } from '../../util/userUtil';

const MODAL_BREAKPOINT = 768; // Search is in modal on mobile layout
const FILTER_DROPDOWN_OFFSET = -14;

function SearchPageComponent(props) {
  const intl = useIntl();
  const [isMobileModalOpen, setIsMobileModalOpen] = useState(false);
  const [currentQueryParams, setCurrentQueryParams] = useState(validUrlQueryParamsFromProps(props));

  const onOpenMobileModal = useCallback(() => setIsMobileModalOpen(true), []);
  const onCloseMobileModal = useCallback(() => setIsMobileModalOpen(false), []);

  const resetAll = useCallback(
    _e => {
      const { navigate, routeConfiguration, config } = props;
      const { listingFields: listingFieldsConfig } = config?.listing || {};
      const { defaultFilters: defaultFiltersConfig } = config?.search || {};
      const urlQueryParams = validUrlQueryParamsFromProps(props);
      const filterQueryParamNames = getQueryParamNames(listingFieldsConfig, defaultFiltersConfig);
      setCurrentQueryParams({});
      const queryParams = omit(urlQueryParams, filterQueryParamNames);
      navigate(createResourceLocatorString('SearchPage', routeConfiguration, {}, queryParams));
    },
    [props]
  );

  const getHandleChangedValueFn = useCallback(
    useNavigatePush => {
      const { navigate, routeConfiguration, config } = props;
      const { listingFields: listingFieldsConfig } = config?.listing || {};
      const { defaultFilters: defaultFiltersConfig, sortConfig } = config?.search || {};
      const urlQueryParams = validUrlQueryParamsFromProps(props);
      return updatedURLParams => {
        // Compute next query params synchronously to avoid stale state on first click
        const { address, bounds, keywords } = urlQueryParams;
        const mergedQueryParams = { ...urlQueryParams, ...currentQueryParams };
        const keywordsMaybe = isMainSearchTypeKeywords(config) ? { keywords } : {};
        const nextQueryParams = {
          ...mergedQueryParams,
          ...updatedURLParams,
          ...keywordsMaybe,
          address,
          bounds,
        };
        setCurrentQueryParams(nextQueryParams);
        if (useNavigatePush) {
          const search = cleanSearchFromConflictingParams(
            nextQueryParams,
            listingFieldsConfig,
            defaultFiltersConfig,
            sortConfig
          );
          navigate(createResourceLocatorString('SearchPage', routeConfiguration, {}, search));
        }
      };
    },
    [props, currentQueryParams]
  );

  const handleSortBy = useCallback(
    (urlParam, values) => {
      const { navigate, routeConfiguration } = props;
      const urlQueryParams = validUrlQueryParamsFromProps(props);
      const queryParams = values
        ? { ...urlQueryParams, [urlParam]: values }
        : omit(urlQueryParams, urlParam);
      navigate(createResourceLocatorString('SearchPage', routeConfiguration, {}, queryParams));
    },
    [props]
  );

  const handleResetAll = useCallback(
    e => {
      resetAll(e);
      if (e && e.currentTarget) {
        e.currentTarget.blur();
      }
    },
    [resetAll]
  );

  const {
    listings = [],
    location,
    pagination = null,
    searchInProgress,
    searchListingsError = null,
    searchParams = {},
    routeConfiguration,
    config,
    currentUser = null,
    scrollingDisabled,
    onManageDisableScrolling,
  } = props;

  const { listingFields: listingFieldsConfig } = config?.listing || {};
  const { defaultFilters: defaultFiltersConfig, sortConfig } = config?.search || {};
  const activeListingTypes = config?.listing?.listingTypes.map(config => config.listingType);
  const marketplaceCurrency = config.currency;

  const { searchParamsAreInSync, urlQueryParams, searchParamsInURL } = searchParamsPicker(
    location.search,
    searchParams,
    listingFieldsConfig,
    defaultFiltersConfig,
    sortConfig,
    isOriginInUse(config)
  );

  const validQueryParams = validFilterParams(
    searchParamsInURL,
    listingFieldsConfig,
    defaultFiltersConfig,
    false
  );

  const isKeywordSearch = isMainSearchTypeKeywords(config);
  const defaultFilters = isKeywordSearch
    ? defaultFiltersConfig.filter(f => f.key !== 'keywords')
    : defaultFiltersConfig;
  const [customPrimaryFilters, customSecondaryFilters] = groupListingFieldConfigs(
    listingFieldsConfig,
    activeListingTypes
  );
  const availableFilters = [...customPrimaryFilters, ...defaultFilters, ...customSecondaryFilters];

  const selectedFilters = validFilterParams(
    validQueryParams,
    listingFieldsConfig,
    defaultFiltersConfig
  );
  const isValidDatesFilter =
    searchParamsInURL.dates == null ||
    (searchParamsInURL.dates != null && searchParamsInURL.dates === selectedFilters.dates);
  const keysOfSelectedFilters = Object.keys(selectedFilters);
  const selectedFiltersCountForMobile = isKeywordSearch
    ? keysOfSelectedFilters.filter(f => f !== 'keywords').length
    : keysOfSelectedFilters.length;

  const hasPaginationInfo = !!pagination && pagination.totalItems != null;
  const totalItems =
    searchParamsAreInSync && hasPaginationInfo
      ? pagination.totalItems
      : pagination?.paginationUnsupported
      ? listings.length
      : 0;
  const listingsAreLoaded =
    !searchInProgress &&
    searchParamsAreInSync &&
    !!(hasPaginationInfo || pagination?.paginationUnsupported);

  const conflictingFilterActive = isAnyFilterActive(
    sortConfig.conflictingFilters,
    validQueryParams,
    listingFieldsConfig,
    defaultFiltersConfig
  );
  const sortBy = mode => {
    return sortConfig.active ? (
      <SortBy
        sort={validQueryParams[sortConfig.queryParamName]}
        isConflictingFilterActive={!!conflictingFilterActive}
        hasConflictingFilters={!!(sortConfig.conflictingFilters?.length > 0)}
        selectedFilters={selectedFilters}
        onSelect={handleSortBy}
        showAsPopup
        mode={mode}
        contentPlacementOffset={FILTER_DROPDOWN_OFFSET}
      />
    ) : null;
  };
  const noResultsInfo = (
    <NoSearchResultsMaybe
      listingsAreLoaded={listingsAreLoaded}
      totalItems={totalItems}
      location={location}
      resetAll={resetAll}
    />
  );

  // Inline i18n: createSearchResultSchema returns title/description/schema, so we keep as is
  const { title, description, schema } = createSearchResultSchema(
    listings,
    searchParamsInURL || {},
    intl,
    routeConfiguration,
    config
  );

  const topbarClasses = isMobileModalOpen
    ? classNames(css.topbarBehindModal, css.topbar)
    : css.topbar;

  const isMentor = getIsMentor(currentUser);
  return (
    <Page
      scrollingDisabled={scrollingDisabled}
      description={description}
      title={title}
      schema={schema}
    >
      <div className="col">
        <TopbarContainer
          rootClassName={topbarClasses}
          currentPage="SearchPage"
          title={isMentor ? 'Mentor Search' : 'Search'}
          currentSearchParams={urlQueryParams}
        />
        <div className={css.layoutWrapperContainer}>
          <MainTabNavVertical tab="search" currentUser={currentUser} />
          <aside className={css.layoutWrapperFilterColumn} data-testid="filterColumnAside">
            <div className={css.filterColumnContent}>
              {availableFilters.map(config => (
                <FilterComponent
                  key={`SearchFiltersMobile.${config.scope || 'built-in'}.${config.key}`}
                  idPrefix="SearchFiltersMobile"
                  className={css.filter}
                  config={config}
                  marketplaceCurrency={marketplaceCurrency}
                  urlQueryParams={urlQueryParams}
                  initialValues={initialValues(props, currentQueryParams)}
                  getHandleChangedValueFn={getHandleChangedValueFn}
                  liveEdit
                  showAsPopup={false}
                  isDesktop
                />
              ))}
              <button className={css.resetAllButton} onClick={handleResetAll}>
                Reset all filters
              </button>
            </div>
          </aside>
          <div className={css.layoutWrapperMain} role="main">
            <div className={css.searchResultContainer}>
              <SearchFiltersMobile
                className={css.searchFiltersMobileList}
                urlQueryParams={validQueryParams}
                sortByComponent={sortBy('mobile')}
                listingsAreLoaded={listingsAreLoaded}
                resultsCount={totalItems}
                searchInProgress={searchInProgress}
                searchListingsError={searchListingsError}
                showAsModalMaxWidth={MODAL_BREAKPOINT}
                onManageDisableScrolling={onManageDisableScrolling}
                onOpenModal={onOpenMobileModal}
                onCloseModal={onCloseMobileModal}
                resetAll={resetAll}
                selectedFiltersCount={selectedFiltersCountForMobile}
                isMapVariant={false}
                noResultsInfo={noResultsInfo}
              >
                {availableFilters.map(config => (
                  <FilterComponent
                    key={`SearchFiltersMobile.${config.scope || 'built-in'}.${config.key}`}
                    idPrefix="SearchFiltersMobile"
                    config={config}
                    marketplaceCurrency={marketplaceCurrency}
                    urlQueryParams={validQueryParams}
                    initialValues={initialValues(props, currentQueryParams)}
                    getHandleChangedValueFn={getHandleChangedValueFn}
                    liveEdit
                    showAsPopup={false}
                  />
                ))}
              </SearchFiltersMobile>
              <MainPanelHeader
                className={css.mainPanel}
                sortByComponent={sortBy('desktop')}
                isSortByActive={sortConfig.active}
                listingsAreLoaded={listingsAreLoaded}
                resultsCount={totalItems}
                searchInProgress={searchInProgress}
                searchListingsError={searchListingsError}
                noResultsInfo={noResultsInfo}
              />
              <div
                className={classNames(css.listingsForGridVariant, {
                  [css.newSearchInProgress]: !(listingsAreLoaded || searchListingsError),
                })}
              >
                {searchListingsError ? (
                  <H3 className={css.error}>
                    There was an error with your search. Please try again.
                  </H3>
                ) : null}
                {!isValidDatesFilter ? (
                  <H5>The selected dates are invalid. Please adjust your filter.</H5>
                ) : null}
                <SearchResultsPanel
                  className={css.searchListingsPanel}
                  listings={listings}
                  pagination={listingsAreLoaded ? pagination : null}
                  search={parse(location.search)}
                  isMapVariant={false}
                />
              </div>
            </div>
          </div>
        </div>
        <FooterContainer />
      </div>
    </Page>
  );
}



SearchPageComponent.propTypes = {
  listings: array,
  onManageDisableScrolling: func.isRequired,
  pagination: propTypes.pagination,
  scrollingDisabled: bool.isRequired,
  searchInProgress: bool.isRequired,
  searchListingsError: propTypes.error,
  searchParams: object,
  currentUser: propTypes.currentUser,
  history: shape({
    push: func.isRequired,
  }).isRequired,
  location: shape({
    search: string.isRequired,
  }).isRequired,
  config: object.isRequired,
  routeConfiguration: arrayOf(propTypes.route).isRequired,
};

const EnhancedSearchPage = props => {
  const config = useConfiguration();
  const routeConfiguration = useRouteConfiguration();
  const navigate = useNavigate();
  const location = useLocation();
  return (
    <SearchPageComponent
      config={config}
      routeConfiguration={routeConfiguration}
      navigate={navigate}
      location={location}
      {...props}
    />
  );
};

const mapStateToProps = state => {
  const {
    currentPageResultIds,
    pagination,
    searchInProgress,
    searchListingsError,
    searchParams,
  } = state.SearchPage;
  const listings = getListingsById(state, currentPageResultIds);
  const { currentUser } = state.user;
  return {
    listings,
    currentUser,
    pagination,
    scrollingDisabled: isScrollingDisabled(state),
    searchInProgress,
    searchListingsError,
    searchParams,
  };
};

const mapDispatchToProps = dispatch => ({
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),
});

const SearchPage = compose(connect(mapStateToProps, mapDispatchToProps))(EnhancedSearchPage);

export default SearchPage;
